import React from 'react';
import { Mail, Send, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';

interface AddProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerType: 'smtp' | 'resend';
  setProviderType: (type: 'smtp' | 'resend') => void;
  providerName: string;
  setProviderName: (name: string) => void;
  smtpHost: string;
  setSmtpHost: (host: string) => void;
  smtpPort: string;
  setSmtpPort: (port: string) => void;
  smtpMode: 'none' | 'starttls' | 'tls';
  setSmtpMode: (mode: 'none' | 'starttls' | 'tls') => void;
  smtpUsername: string;
  setSmtpUsername: (username: string) => void;
  smtpPassword: string;
  setSmtpPassword: (password: string) => void;
  smtpFrom: string;
  setSmtpFrom: (from: string) => void;
  smtpFromName: string;
  setSmtpFromName: (fromName: string) => void;
  showSmtpPassword: boolean;
  setShowSmtpPassword: (show: boolean) => void;
  isTestingSmtp: boolean;
  smtpTestResult: {
    success: boolean;
    message?: string;
    error?: string;
  } | null;
  resendApiKey: string;
  setResendApiKey: (key: string) => void;
  resendFrom: string;
  setResendFrom: (from: string) => void;
  isSavingProvider: boolean;
  onTestSmtp: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const AddProviderModal: React.FC<AddProviderModalProps> = ({
  isOpen,
  onClose,
  providerType,
  setProviderType,
  providerName,
  setProviderName,
  smtpHost,
  setSmtpHost,
  smtpPort,
  setSmtpPort,
  smtpMode,
  setSmtpMode,
  smtpUsername,
  setSmtpUsername,
  smtpPassword,
  setSmtpPassword,
  smtpFrom,
  setSmtpFrom,
  smtpFromName,
  setSmtpFromName,
  showSmtpPassword,
  setShowSmtpPassword,
  isTestingSmtp,
  smtpTestResult,
  resendApiKey,
  setResendApiKey,
  resendFrom,
  setResendFrom,
  isSavingProvider,
  onTestSmtp,
  onSubmit,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configure Bring-Your-Own-Provider"
      maxWidth="lg"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Provider Type Selection */}
        <div>
          <label className="block text-xs font-medium text-cf-textSecondary mb-1.5">
            Provider Connector Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(['smtp', 'resend'] as const).map((t) => (
              <Button
                key={t}
                type="button"
                variant={providerType === t ? 'orange-subtle' : 'outline'}
                size="sm"
                onClick={() => setProviderType(t)}
                leftIcon={t === 'smtp' ? <Mail size={14} /> : <Send size={14} />}
                className="uppercase font-semibold"
              >
                {t}
              </Button>
            ))}
          </div>
        </div>

        <Input
          label="Provider Display Name"
          type="text"
          size="xs"
          required
          value={providerName}
          onChange={(e) => setProviderName(e.target.value)}
          placeholder="e.g. Production Mailpit / Corporate SMTP"
        />

        {/* Type-Specific Fields */}
        {providerType === 'smtp' && (
          <div className="space-y-3 p-3 bg-cf-bg rounded border border-cf-border">
            {smtpTestResult && (
              <div
                className={`p-2.5 rounded border text-xs flex items-start gap-2 ${
                  smtpTestResult.success
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'
                }`}
              >
                {smtpTestResult.success ? (
                  <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <span className="font-semibold block">
                    {smtpTestResult.success ? '✓ Connection Verified' : '✕ Connection Test Failed'}
                  </span>
                  <span className="block mt-0.5">
                    {smtpTestResult.success ? smtpTestResult.message : smtpTestResult.error}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Host"
                type="text"
                size="xs"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
              />
              <Input
                label="Port"
                type="number"
                size="xs"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
              />
            </div>

            <Select
              label="Encryption Mode"
              size="xs"
              value={smtpMode}
              onChange={(e) => setSmtpMode(e.target.value as any)}
              options={[
                { value: 'none', label: 'None (Local / Mailpit :1025)' },
                { value: 'starttls', label: 'STARTTLS (Port 587)' },
                { value: 'tls', label: 'Direct TLS/SSL (Port 465)' },
              ]}
            />

            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Username / Login"
                type="text"
                size="xs"
                value={smtpUsername}
                onChange={(e) => setSmtpUsername(e.target.value)}
                placeholder="postmaster@domain.com"
              />

              <div>
                <Input
                  label="Password"
                  type={showSmtpPassword ? 'text' : 'password'}
                  size="xs"
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder="••••••••"
                  rightAction={
                    <Button
                      type="button"
                      size="icon-xs"
                      variant="ghost"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    >
                      {showSmtpPassword ? <EyeOff size={12} /> : <Eye size={12} />}
                    </Button>
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                label="From Address"
                type="email"
                size="xs"
                value={smtpFrom}
                onChange={(e) => setSmtpFrom(e.target.value)}
              />
              <Input
                label="From Name"
                type="text"
                size="xs"
                value={smtpFromName}
                onChange={(e) => setSmtpFromName(e.target.value)}
              />
            </div>

            <div className="pt-1">
              <Button
                variant="secondary"
                size="xs"
                type="button"
                onClick={onTestSmtp}
                disabled={isTestingSmtp}
                isLoading={isTestingSmtp}
                leftIcon={<CheckCircle2 size={12} className="text-emerald-400" />}
                className="w-full font-semibold"
              >
                Test SMTP Handshake
              </Button>
            </div>
          </div>
        )}

        {providerType === 'resend' && (
          <div className="space-y-3 p-3 bg-cf-bg rounded border border-cf-border">
            <Input
              label="Resend API Key"
              type="password"
              size="xs"
              required
              value={resendApiKey}
              onChange={(e) => setResendApiKey(e.target.value)}
              placeholder="re_123456789..."
            />
            <Input
              label="Verified From Address"
              type="email"
              size="xs"
              required
              value={resendFrom}
              onChange={(e) => setResendFrom(e.target.value)}
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
          <Button variant="secondary" size="xs" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xs"
            type="submit"
            isLoading={isSavingProvider}
            disabled={isSavingProvider}
          >
            Save Provider
          </Button>
        </div>
      </form>
    </Modal>
  );
};
